import { BsPrinterFill, BsUpcScan } from 'react-icons/bs';
import { useAppSelector } from '../../store/hooks';
import type { PrinterState } from '../../store/slices/printerSlice';

/**
 * Top-bar printer indicators. One badge per printer known to zebraman,
 * showing the printer glyph + WiFi % + queued job count. Disconnected
 * printers flash via the `hc-printer-flash` keyframe defined inline.
 */
export default function PrinterIndicators() {
  const printers = useAppSelector((s) => s.printer.printers);
  const showScannerIndicator = useAppSelector((s) => s.ui.showScannerIndicator);

  if (!printers || printers.length === 0) return null;

  return (
    <>
      <style>{`
        @keyframes hc-printer-flash {
          0%, 100% { opacity: 1; }
          50%      { opacity: 0.25; }
        }
        .hc-printer-indicator {
          display: inline-flex;
          align-items: center;
          gap: 4px;
          padding: 4px 8px;
          margin-right: 8px;
          border-radius: 6px;
          background: rgba(255, 255, 255, 0.08);
          color: #fff;
          font-size: 0.85rem;
          line-height: 1;
          font-variant-numeric: tabular-nums;
        }
        .hc-printer-indicator.disconnected {
          background: rgba(220, 53, 69, 0.35);
          animation: hc-printer-flash 1s infinite;
        }
        .hc-printer-indicator.weak-wifi { background: rgba(255, 193, 7, 0.35); }
        .hc-printer-indicator .hc-pi-num { font-weight: 600; }
        .hc-printer-indicator .hc-pi-sep { opacity: 0.55; padding: 0 2px; }
        .hc-scanner-indicator {
          display: inline-flex;
          align-items: center;
          padding: 4px 6px;
          margin-right: 8px;
          margin-left: -4px;
          border-radius: 6px;
          background: rgba(255, 255, 255, 0.08);
          color: #fff;
          font-size: 0.85rem;
          line-height: 1;
        }
        .hc-scanner-indicator.disconnected {
          background: rgba(220, 53, 69, 0.35);
          animation: hc-printer-flash 1s infinite;
        }
      `}</style>
      {printers.map((p: PrinterState) => {
        const wifi = p.wifi_signal;
        const isWeak = wifi !== null && wifi < 60;
        const cls = [
          'hc-printer-indicator',
          !p.connected ? 'disconnected' : '',
          p.connected && isWeak ? 'weak-wifi' : '',
        ].filter(Boolean).join(' ');
        const title = [
          p.sn,
          p.location ? `@ ${p.location}` : null,
          `WiFi: ${wifi ?? '?'}%`,
          `Queue: ${p.queue_len}`,
          p.connected ? 'connected' : 'DISCONNECTED',
        ].filter(Boolean).join(' • ');
        const scannerKnown = p.scanner_connected !== null && p.scanner_connected !== undefined;
        const scannerDisconnected = scannerKnown && p.scanner_connected === false;
        const scannerCls = [
          'hc-scanner-indicator',
          scannerDisconnected ? 'disconnected' : '',
        ].filter(Boolean).join(' ');
        const scannerTitle = [
          p.sn,
          scannerKnown
            ? (p.scanner_connected ? 'Scanner connected' : 'Scanner DISCONNECTED')
            : 'Scanner state unknown',
        ].join(' • ');
        return (
          <span key={p.sn} style={{ display: 'inline-flex', alignItems: 'center' }}>
            <span className={cls} title={title}>
              <BsPrinterFill aria-hidden="true" />
              <span className="hc-pi-num">{wifi ?? '?'}</span>
              <span className="hc-pi-sep">|</span>
              <span className="hc-pi-num">{p.queue_len}</span>
            </span>
            {showScannerIndicator && scannerKnown && (
              <span className={scannerCls} title={scannerTitle}>
                <BsUpcScan aria-hidden="true" />
              </span>
            )}
          </span>
        );
      })}
    </>
  );
}
